import { useEffect, useState } from "react";
import { CloseButton, Form, Offcanvas } from "react-bootstrap";
import { Button as ButtonAntd, Select, Checkbox, Input, Tooltip, Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlask, faEdit, faCopy } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const { TextArea } = Input;
const { Option } = Select;

const APP_OPTIONS = [
  { value: "olitask-main-service", label: "Olitask Main Service" },
  { value: "oliveeb-gateway-service", label: "Gateway Service" },
  { value: "oliveeb-identity-service", label: "Identity Service" },
  { value: "oliveeb-profile-service", label: "Profile Service" },
  { value: "oliveeb-payment-service", label: "Payment Service" },
  { value: "oliveeb-storage-service", label: "Storage Service" },
  { value: "oliveeb-notification-service", label: "Notification Service" },
  { value: "oliveeb-chat-service", label: "Chat Service" },
  { value: "oliveeb-communication-service", label: "Communication Service" },
  { value: "oliveeb-email-service", label: "Email Service" },
  { value: "oliveeb-ai-service", label: "AI Service" },
  { value: "oliveeb-pm-service", label: "PM Service" },
  { value: "oliveeb-code-service", label: "Code Service" },
  { value: "oliveeb-cache-service", label: "Cache Service" },
  { value: "oliveeb-social-service", label: "Social Service" },
  { value: "anonymous-service", label: "Anonymous Service" },
];
const FRONTEND_CHECK_INTERVAL_MS = 30000;

export default function HealthCheckDrawer({ healthCheck, show, onClose, onSave, onProbe, isSubmitting }) {
  const [visible, setVisible] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDefaultPing, setIsDefaultPing] = useState(true);
  const [originalForm, setOriginalForm] = useState(null);
  const [protocol, setProtocol] = useState("https://");
  const [form, setForm] = useState({});
  const [now, setNow] = useState(Date.now());
  const [backendLastUpdatedAt, setBackendLastUpdatedAt] = useState(null);
  const [frontendLastCheckedAt, setFrontendLastCheckedAt] = useState(null);


  const formatRelativeTime = (timestamp, currentTime) => {
    if (!timestamp) return "Never";

    const diff = Math.floor((currentTime - new Date(timestamp)) / 1000);

    if (diff < 5) return "just now";
    if (diff < 60) return `${diff} seconds ago`;

    const mins = Math.floor(diff / 60);
    if (mins < 60)
      return `${mins} minute${mins !== 1 ? "s" : ""} ago`;

    const hours = Math.floor(mins / 60);
    if (hours < 24)
      return `${hours} hour${hours !== 1 ? "s" : ""} ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  };


  const isNewRecord = !healthCheck;

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!show) {
      setIsEditingMode(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      setFrontendLastCheckedAt(null);
      return;
    }

    setFrontendLastCheckedAt(Date.now());
    const interval = setInterval(() => {
      setFrontendLastCheckedAt(Date.now());
    }, FRONTEND_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [show]);

  useEffect(() => {
    if (!healthCheck) {
      setBackendLastUpdatedAt(null);
      return;
    }

    setBackendLastUpdatedAt(
      healthCheck.lastUpdatedAt ?? healthCheck.lastCheckedAt ?? null
    );
  }, [healthCheck]);



  useEffect(() => {
    if (!show) return;

    setVisible(true);

    if (healthCheck) {
      const urlValue = healthCheck.url || "";
      const normalizedWhoPinged =
        healthCheck.whoPinged || "oliveeb-gateway-service";
      const normalizedExpectedStatus =
        parseInt(
          healthCheck.expectedStatus ?? healthCheck.expectedStatusCode ?? 200
        ) || 200;

      let formattedForm = {
        ...healthCheck,
        whoPinged: normalizedWhoPinged,
        expectedStatus: normalizedExpectedStatus,
        lastCheckedAt: healthCheck.lastCheckedAt ?? null,
        lastResponseMessage:
          healthCheck.lastResponseMessage ?? healthCheck.lastCheckMessage ?? null,
        active:
          typeof healthCheck.active === "boolean"
            ? healthCheck.active
            : !!healthCheck.isEnabled,
      };

      if (urlValue.startsWith("http://")) {
        setProtocol("http://");
        formattedForm.url = urlValue.slice(7);
      } else if (urlValue.startsWith("https://")) {
        setProtocol("https://");
        formattedForm.url = urlValue.slice(8);
      } else {
        setProtocol("https://");
      }


      setForm(formattedForm);

      setOriginalForm(JSON.parse(JSON.stringify(formattedForm)));

      setIsEditingMode(false);

      setIsDefaultPing(
        normalizedWhoPinged === "oliveeb-gateway-service" &&
        normalizedExpectedStatus === 200
      );

    } else {
      const newForm = {
        name: "",
        oliveebApp: "",
        url: "",
        description: "",
        active: false,
        whoPinged: "oliveeb-gateway-service",
        expectedStatus: 200,
        lastCheckedAt: null
      };

      setForm(newForm);
      setOriginalForm(JSON.parse(JSON.stringify(newForm)));

      setProtocol("https://");
      setIsEditingMode(true);
      setIsDefaultPing(true);
    }
  }, [show, healthCheck]);


  const handleClose = () => {
    setVisible(false);

    if (isNewRecord) {
      setForm({
        name: "",
        oliveebApp: "",
        url: "",
        description: "",
        active: false,
        whoPinged: "oliveeb-gateway-service",
        expectedStatus: 200,
        lastCheckedAt: null
      });
    }

    setTimeout(onClose, 300);
  };


  const resetFormToOriginal = () => {
    if (originalForm) {
      setForm(JSON.parse(JSON.stringify(originalForm)));
    }
  };


  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCopy = () => {
    const fullUrl = `${protocol}${form.url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success("URL copied to clipboard!");
  };

  // --- VALIDATION & CLEANING ---
  const validateAndClean = () => {
    const trimmedUrl = form.url?.trim().replace(/^(https?:\/\/)/, "") || "";
    const normalizedExpectedStatus =
      parseInt(form.expectedStatus ?? form.expectedStatusCode ?? 200) || 200;

    // Validation for @NotBlank/@NotNull fields
    if (!form.name?.trim()) {
      toast.error("Service Name is required");
      return null;
    }
    if (!form.oliveebApp) {
      toast.error("Target Application is required");
      return null;
    }
    if (!trimmedUrl) {
      toast.error("URL path is required");
      return null;
    }

    if (!form.whoPinged) {
      toast.error("Who Pinged is required");
      return null;
    }

    // Returns the exact structure of your HealthCheckRequestDto record
    return {
      oliveebApp: form.oliveebApp,          // @NotNull
      name: form.name.trim(),               // @NotBlank
      description: form.description || "",   // Optional String
      url: `${protocol}${trimmedUrl}`,      // @NotBlank
      expectedStatusCode: normalizedExpectedStatus, // Integer
      // New records must stay disabled by default; test/probe should not alter this.
      isEnabled: isNewRecord ? false : form.active === true,
      whoPinged: form.whoPinged,
      // Intentionally do not send probe diagnostics/state on save.
    };
  };

  const onFinalSave = () => {
    const dtoList = validateAndClean();
    if (dtoList) {
      onSave(dtoList); // This sends the exact record structure to your save endpoint
    }
  };


  const handleTestConnection = async () => {
    const validatedData = validateAndClean();
    if (!validatedData) return;

    setIsTesting(true);

    try {
      const res = await onProbe(validatedData);
      const { statusCode, message } = res || {};
      const expectedStatus =
        parseInt(form.expectedStatus ?? form.expectedStatusCode ?? 200) || 200;
      const healthy = statusCode === expectedStatus;

      setForm(prev => {
        const updated = {
          ...prev,
          lastStatusCode: statusCode,
          lastResponseMessage: message,
          isHealthy: healthy,
          lastCheckedAt: new Date().toISOString()
        };

        // Only persist snapshot if existing record
        if (!isNewRecord) setOriginalForm(JSON.parse(JSON.stringify(updated)));

        return updated;
      });

      if (healthy) toast.success(`Success! Backend received ${statusCode}`);
      else toast.warning(`Connected, but got status ${statusCode}: ${message}`);

    } catch (err) {
      setForm(prev => {
        const updated = {
          ...prev,
          lastStatusCode: "ERROR",
          lastResponseMessage: "Service could not be reached",
          isHealthy: false,
          lastCheckedAt: new Date().toISOString()
        };

        if (!isNewRecord) setOriginalForm(JSON.parse(JSON.stringify(updated)));

        return updated;
      });

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      toast.error(`Probe failed: ${backendMessage || "The service could not be reached."}`);
    } finally {
      setIsTesting(false);
    }
  };



  // Styles matching Routes Table/Drawer
  const labelStyle = { fontSize: '0.75rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: '4px' };
  const valueStyle = { fontSize: '0.65rem', fontWeight: 500, color: '#5e6e82' };
  const pillBase = { padding: "3px 8px", borderRadius: "6px", fontSize: "0.65rem", fontWeight: 600, border: '1px solid' };

  const selectBefore = (
    <Select value={protocol} onChange={setProtocol} style={{ width: 90 }} disabled={!isEditingMode}>
      <Option value="https://">https://</Option>
      <Option value="http://">http://</Option>
    </Select>
  );

  const getSeverityStyle = (val) => {
    const map = {
      CRITICAL: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      MEDIUM: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      ALERT: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      WARNING: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      INFO: { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" }
    };
    const s = map[val] || map.INFO;
    return { ...pillBase, backgroundColor: s.bg, color: s.text, borderColor: s.border };
  };

  const getStatusStyle = (healthy) => {
    if (healthy !== true && healthy !== false) {
      return {
        ...pillBase,
        backgroundColor: "#f3f4f6",
        color: "#6b7280",
        borderColor: "#d1d5db",
      };
    }
    return healthy
      ? { ...pillBase, backgroundColor: "#e8f7e3", color: "#2e7d32", borderColor: "#d5ebd0" }
      : { ...pillBase, backgroundColor: "#fbe9e7", color: "#c62828", borderColor: "#f5c6cb" };
  };

  return (
    <Offcanvas
      show={visible}
      onHide={handleClose}
      placement="end"
      backdrop="static"
      scroll={false}
      style={{ width: "30rem" }}
      className="border-0 d-flex flex-column"
    >
      <style>
        {`
          @keyframes statusDotBlink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.35; transform: scale(0.85); }
          }
          .copy-url-btn.ant-btn {
            background: #f3f4f6;
            border-color: #d1d5db;
            color: #4b5563;
            transition: all 0.2s ease;
          }
          .copy-url-btn.ant-btn:hover,
          .copy-url-btn.ant-btn:focus {
            background: #dbeafe !important;
            border-color: #93c5fd !important;
            color: #1d4ed8 !important;
          }
        `}
      </style>
      <div className="offcanvas-header px-4">
        <h5 style={{
          fontWeight: 700,
          fontSize: '1rem',
          color: '#166534'
        }}>
          {isNewRecord ? "Create Health Check" : "Health Check Details"}</h5>
        <CloseButton className="btn-sm" onClick={handleClose} />
      </div>

      <div className="flex-grow-1 overflow-auto px-4">
        {/* URL Group with Protocol Dropdown */}
        <Form.Group className="mb-4">
          <div style={labelStyle}>Check URL</div>
          {isEditingMode ? (
            <Space.Compact style={{ width: "100%" }}>
              {selectBefore}
              <Input
                className="font-monospace"
                value={form.url}
                onChange={(e) => updateField("url", e.target.value)}
                placeholder="api.example.com/health"
                style={{ width: "100%" }}
              />
            </Space.Compact>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <div className="font-monospace p-2 rounded flex-grow-1" style={{
                fontSize: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb'
              }}>
                <span className="text-muted">{protocol}</span>{form.url}
              </div>
              <Tooltip title="Copy URL">
                <ButtonAntd
                  size="small"
                  className="copy-url-btn"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  onClick={handleCopy}
                />
              </Tooltip>
            </div>
          )}
        </Form.Group>

        <div className="d-flex flex-column gap-3 mb-4">
          <div>
            <div style={labelStyle}>Health Check Name</div>
            {isEditingMode ? <Input size="small" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="eg Identity health check"/> : <div style={valueStyle}>{form.name || '---'}</div>}
          </div>

          <div>
            <div style={labelStyle}>Target App / Service</div>
            {isEditingMode ? (
              <Select
                showSearch  // Enables searching
                size="small"
                style={{ width: '100%' }}
                options={APP_OPTIONS}
                value={form.oliveebApp || undefined}
                placeholder="Choose an option"
                optionFilterProp="label" // Search against the label text
                onChange={(v) => updateField("oliveebApp", v)}
              />
            ) : (
              <div style={valueStyle}>{form.oliveebApp || '---'}</div>
            )}
          </div>

          {/* RESPONSE DIAGNOSTICS */}
          {(form.lastStatusCode || form.lastResponseMessage || frontendLastCheckedAt) && (
            <div className="mb-4">

              {/* Header */}
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: "#166534",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                }}
              >
                Response Diagnostics
              </div>

              {/* Card */}
              <div
                style={{
                  backgroundColor: "#f3fbf6",
                  border: "1px solid #d1f2e0",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >

                {/* Top Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >

                  {/* Left: Status */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

                    {/* Dot */}
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor:
                          form.isHealthy === true
                            ? "#16a34a"
                            : form.isHealthy === false
                            ? "#dc2626"
                            : "#6b7280",
                        animation: "statusDotBlink 2.2s ease-in-out infinite",
                      }}
                    />

                    {/* Code */}
                    <span
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        color:
                          form.isHealthy === true
                            ? "#166534"
                            : form.isHealthy === false
                            ? "#991b1b"
                            : "#4b5563",
                      }}
                    >
                      {form.lastStatusCode || "---"}
                    </span>

                    {/* Label */}
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "#6b7280",
                      }}
                    >
                      {form.isHealthy === true
                        ? "Healthy"
                        : form.isHealthy === false
                        ? "Unhealthy"
                        : "Unknown"}
                    </span>

                  </div>

                  {/* Last Checked */}
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Last Updated At:{" "}
                    <span style={{ fontWeight: 600 }}>
                      {backendLastUpdatedAt
                        ? formatRelativeTime(backendLastUpdatedAt, now)
                        : "Not yet"}
                    </span>
                  </div>

                </div>

                {/* Message */}
                <div
                  className="font-monospace"
                  style={{
                    fontSize: "0.75rem",
                    lineHeight: "1.5",
                    color:
                      form.isHealthy === false ? "#b91c1c" : "#1f2937",
                    backgroundColor: "#ffffff",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    wordBreak: "break-word",
                  }}
                >
                  {form.lastResponseMessage ||
                    "No response message recorded yet."}
                </div>

                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "0.68rem",
                    color: "#6b7280",
                  }}
                >
                  Last checked (every 30s):{" "}
                  <span style={{ fontWeight: 600 }}>
                    {frontendLastCheckedAt
                      ? formatRelativeTime(frontendLastCheckedAt, now)
                      : "Not yet"}
                  </span>
                </div>

              </div>

            </div>
          )}


          <div className="pt-4 mt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
            <div style={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              color: '#166534',
              marginBottom: '8px',
              textTransform: 'uppercase'
            }}>
              Monitoring Logic
            </div>

            <div
              className="p-3 rounded"
              style={{
                backgroundColor: '#f3fbf6',
                border: '1px solid #d1f2e0',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'

              }}
            >
              <Checkbox
                checked={isDefaultPing}
                disabled={!isEditingMode}
                onChange={(e) => {
                  setIsDefaultPing(e.target.checked);
                  if (e.target.checked)
                    // Safer way to write the same logic:
                    setForm(prev => ({
                      ...prev,
                      whoPinged: "oliveeb-gateway-service",
                      expectedStatus: 200
                    }));
                }}
              >
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#1f2937'
                }}>
                  Use Default Gateway Ping
                  <span style={{
                    color: '#16a34a',
                    fontWeight: 600,
                    marginLeft: '6px'
                  }}>
                    (200 OK)
                  </span>
                </span>
              </Checkbox>

              {!isDefaultPing && (
                <div
                  className="mt-3 ps-3"
                  style={{
                    borderLeft: '3px solid #22c55e'
                  }}
                >
                  {/* Pinger Service */}
                  <div className="mb-3">
                    <div style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: '#166534',
                      marginBottom: '4px',
                      letterSpacing: '0.05em'
                    }}>
                      PINGER SERVICE
                    </div>

                    <Select
                      showSearch
                      size="small"
                      style={{
                        width: '100%',
                        maxWidth: '260px',
                        backgroundColor: '#ffffff'
                      }}
                      disabled={!isEditingMode}
                      value={form.whoPinged}
                      options={APP_OPTIONS}
                      placeholder="Search pinger..."
                      optionFilterProp="label"
                      onChange={(v) => updateField("whoPinged", v)}
                    />
                  </div>

                  {/* Expected Status */}
                  <div>
                    <div style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: '#166534',
                      marginBottom: '4px',
                      letterSpacing: '0.05em'
                    }}>
                      EXPECTED STATUS
                    </div>

                    <Input
                      size="small"
                      type="number"
                      style={{
                        width: '90px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d1d5db',
                        color: '#1f2937'
                      }}
                      disabled={!isEditingMode}
                      value={form.expectedStatus}
                      onChange={(e) =>
                        updateField("expectedStatus", e.target.value)
                      }
                      placeholder="200"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>


          <div>
            <div style={labelStyle}>Notes / Description</div>
            <TextArea
              rows={4}
              disabled={!isEditingMode}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="No description provided"
              style={{
                fontSize: '0.7rem',
                minHeight: '110px',
                borderRadius: '6px',
                borderColor: '#d1d5db'
              }}
            />

          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="border-top p-3 bg-light d-flex justify-content-between align-items-center">

        {/* --- SCENARIO 1: CREATING A NEW RECORD --- */}
        {isNewRecord ? (
          <>
            <div className="d-flex gap-2">
              <ButtonAntd
                type="primary"
                loading={isSubmitting}
                className="bg-=order-0 text-xs fw-bold"
                onClick={onFinalSave}
              >
                Create Check
              </ButtonAntd>

              <ButtonAntd
                className="bg-info text-white border-0 text-xs fw-bold"
                icon={<FontAwesomeIcon icon={faFlask} />}
                loading={isTesting}
                onClick={handleTestConnection}
              >
                Run Test
              </ButtonAntd>
            </div>

            <ButtonAntd
              className="bg-secondary text-white border-0 text-xs fw-bold"
              onClick={handleClose}
            >
              Close
            </ButtonAntd>
          </>
        ) : (
          /* --- SCENARIO 2: EXISTING RECORD --- */
          <>
            <div className="d-flex gap-2">
              {isEditingMode ? (
                /* EDIT MODE */
                <>
                  <ButtonAntd
                    type="primary"
                    loading={isSubmitting}
                    className="bg-success border-0 text-xs fw-bold"
                    onClick={onFinalSave}
                  >
                    Save Changes
                  </ButtonAntd>

                  <ButtonAntd
                    className="bg-info text-white border-0 text-xs fw-bold"
                    icon={<FontAwesomeIcon icon={faFlask} />}
                    loading={isTesting}
                    onClick={handleTestConnection}
                  >
                    Run Test
                  </ButtonAntd>

                  <ButtonAntd
                    className="border text-xs fw-bold"
                    onClick={() => {
                      resetFormToOriginal();
                      setIsEditingMode(false);
                    }}
                  >
                    Cancel
                  </ButtonAntd>
                </>
              ) : (
                /* VIEW MODE */
                <ButtonAntd
                  type="primary"
                  className="bg-success border-0 text-xs fw-bold"
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={() => setIsEditingMode(true)}
                >
                  Edit Configuration
                </ButtonAntd>
              )}
            </div>

            {/* Close button shown when NOT editing an existing record */}
            {!isEditingMode && (
              <ButtonAntd
                className="bg-secondary text-white border-0 text-xs fw-bold"
                onClick={handleClose}
              >
                Close
              </ButtonAntd>
            )}
          </>
        )}
      </div>

    </Offcanvas>
  );
}


